import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart } from 'lucide-react';
import { normCat, CATEGORY_IMAGES, CATEGORY_ICONS, EuroLeafImg } from './constants';

const BentoCategoryCard = ({ category, className = '', span = '' }) => {
    const catId = normCat(category.id);
    const image = CATEGORY_IMAGES[catId] || category.image_url;
    const icon = CATEGORY_ICONS[catId];
    const isBio = catId === 'cat-bio';

    return (
        <Link
            to={`/catalogue?category=${catId}`}
            className={`group relative overflow-hidden rounded-3xl ${span} ${className}`}
            data-testid={`bento-${catId}`}
        >
            <div className="absolute inset-0">
                <img src={image} alt={category.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
            </div>
            {isBio && (
                <div className="absolute top-4 right-4 flex items-center gap-1.5 z-10">
                    <EuroLeafImg size={28} />
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#009845] shadow-lg" title="AB Agriculture Biologique">
                        <span className="text-white font-black text-[11px] leading-none tracking-tight">AB</span>
                    </span>
                </div>
            )}
            <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
                <span className="text-3xl mb-2">{icon}</span>
                <h3 className="text-white font-bold text-xl md:text-2xl drop-shadow-lg">{category.name}</h3>
                <p className="text-white/70 text-sm mt-1 line-clamp-2">{category.description}</p>
                <div className="mt-3 flex items-center text-white/80 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Découvrir <ArrowRight className="ml-1 w-4 h-4" />
                </div>
            </div>
        </Link>
    );
};

export const BentoGrid = ({ bentoCats, loading }) => (
    <section className="py-16 md:py-24 bg-[#FAFAF5]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">Nos Univers</p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
                    Nos Familles de Produits
                </h2>
                <p className="text-gray-500 mt-3 max-w-lg mx-auto">
                    Une sélection rigoureuse de produits frais et de qualité pour votre commerce
                </p>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 h-[500px]">
                    {[1,2,3,4].map(i => <div key={i} className="bg-gray-200 animate-pulse rounded-3xl" />)}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[240px] lg:auto-rows-[280px]">
                    {bentoCats[0] && (
                        <BentoCategoryCard category={bentoCats[0]} span="md:col-span-2 md:row-span-2" />
                    )}
                    {bentoCats[1] && <BentoCategoryCard category={bentoCats[1]} />}
                    {bentoCats[2] && <BentoCategoryCard category={bentoCats[2]} />}
                    {bentoCats.slice(3).map(cat => (
                        <BentoCategoryCard key={cat.id} category={cat} />
                    ))}
                    {bentoCats.length <= 2 && (
                        <Link to="/catalogue" className="rounded-3xl bg-emerald-600 flex flex-col items-center justify-center p-8 text-white hover:bg-emerald-700 transition-colors" data-testid="bento-cta">
                            <ShoppingCart className="w-10 h-10 mb-3 opacity-80" />
                            <span className="font-bold text-lg">Voir tout le catalogue</span>
                            <ArrowRight className="w-5 h-5 mt-2" />
                        </Link>
                    )}
                </div>
            )}
        </div>
    </section>
);
